import type { NativeGitStatus } from '@crowdex/workspace-protocol';
import { useEffect } from 'react';
import { fetchCloudTaskPullRequest } from '@/lib/cloud-runtime/cloud-runtime';
import { selectWorktreeSourceRoot } from '@/lib/desktop-shell/conversation-worktree';
import type { DesktopShellStore, SyncedWorkspace } from '@/lib/desktop-shell/store';
import { claimLocalConversationPullRequest } from '@/lib/local-conversations';
import { isLocalMachine } from '@/lib/local-runtime';
import { readNativeGitStatus } from '@/lib/native-desktop';
import { claimedPullRequest, pullRequestForBranch } from '@/lib/pull-request';

/**
 * Records with the server the pull request just opened from a conversation (its Create PR
 * dialog) as that conversation's own, so that it keeps it in a folder other conversations share
 * (the main checkout), on every client.
 */
export async function recordConversationPullRequest(
  store: DesktopShellStore,
  conversationId: string,
  workspace: string,
  url: string | null,
): Promise<void> {
  const status = await readNativeGitStatus(workspace).catch(() => null);
  const pullRequest = status?.pullRequest ?? null;
  const branch = pullRequest?.headBranch || status?.branch;
  if (!branch) return;
  const claim = await claimLocalConversationPullRequest(conversationId, {
    branch,
    url: url ?? pullRequest?.url ?? null,
  }).catch(() => null);
  if (claim === null) return;
  const { setSyncedThreads, setSyncedPullRequests } = store.getState();
  // The server took the branch from any other conversation of the folder.
  setSyncedThreads((current) =>
    current.map((thread) =>
      thread.id === conversationId
        ? { ...thread, pullRequest: claim }
        : thread.pullRequest?.branch === claim.branch
          ? { ...thread, pullRequest: null }
          : thread,
    ),
  );
  if (pullRequest !== null)
    setSyncedPullRequests((current) => ({ ...current, [conversationId]: pullRequest }));
}

/** Synchronizes local and cloud pull-request summaries with the desktop-shell store. */
export function usePullRequestSync({
  store,
  preview,
  workspace,
  chatId,
  gitStatus,
  syncedWorkspaces,
  executionMode = 'local',
}: {
  store: DesktopShellStore;
  preview: boolean;
  workspace: string | null;
  chatId: string;
  gitStatus: NativeGitStatus | null;
  syncedWorkspaces: readonly SyncedWorkspace[];
  executionMode?: 'local' | 'worktree' | 'cloud';
}): void {
  useEffect(() => {
    if (preview || !isLocalMachine()) return;
    const roots = new Set<string>();
    for (const entry of syncedWorkspaces) {
      if (entry.runtime !== 'cloud') roots.add(entry.rootPath);
    }
    if (roots.size === 0) return;
    let cancelled = false;
    void Promise.all(
      [...roots].map(async (root) => {
        try {
          const status = await readNativeGitStatus(root);
          if (cancelled || status.pullRequest === null) return;
          store
            .getState()
            .setWorkspacePullRequests((current) =>
              current[root] === status.pullRequest
                ? current
                : { ...current, [root]: status.pullRequest },
            );
        } catch {
          return;
        }
      }),
    );
    return () => {
      cancelled = true;
    };
  }, [preview, store, syncedWorkspaces]);

  useEffect(() => {
    if (preview || workspace === null || gitStatus === null) return;
    if (executionMode === 'cloud') return;
    const { setWorkspacePullRequests, setSyncedPullRequests } = store.getState();
    setWorkspacePullRequests((current) => ({
      ...current,
      [workspace]: pullRequestForBranch(
        gitStatus.pullRequest,
        current[workspace] ?? null,
        gitStatus.branch,
      ),
    }));
    // A conversation's own pull request: its own worktree's, else the one the server records its
    // work is on, kept up to date while its branch is checked out. A folder it shares gives it no
    // other's.
    const state = store.getState();
    const ownFolder = selectWorktreeSourceRoot(state) !== null;
    const claim = state.syncedThreads.find((thread) => thread.id === chatId)?.pullRequest ?? null;
    setSyncedPullRequests((current) => {
      const recorded = current[chatId] ?? null;
      const next = ownFolder
        ? pullRequestForBranch(gitStatus.pullRequest, recorded, gitStatus.branch)
        : claimedPullRequest(claim, gitStatus.pullRequest, recorded);
      return next === recorded ? current : { ...current, [chatId]: next };
    });
  }, [chatId, executionMode, gitStatus, preview, store, workspace]);

  useEffect(() => {
    const { setCloudPullRequest, setSyncedPullRequests } = store.getState();
    if (preview || executionMode !== 'cloud') {
      setCloudPullRequest(null);
      return;
    }
    setCloudPullRequest(store.getState().syncedPullRequests[chatId] ?? null);
    let cancelled = false;
    void fetchCloudTaskPullRequest(chatId)
      .then((pullRequest) => {
        if (cancelled) return;
        setCloudPullRequest(pullRequest);
        setSyncedPullRequests((current) => ({ ...current, [chatId]: pullRequest }));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [chatId, executionMode, preview, store]);
}
